import React, { useCallback, useState } from "react";

import DetachedModal from "../../../Shared/Modals/DetachedModal/DetachedModal";

import { SpaceScannerStage1 } from "./SpaceScannerStage1/SpaceScannerStage1";
import { SpaceScannerStage2 } from "./SpaceScannerStage2/SpaceScannerStage2";
import { SpaceScannerResult } from "./SpaceScannerResult/SpaceScannerResult";
import {
  ProcessedScanResult,
  ScanResults,
  ScanConfiguration,
  OriginalAssignment
} from "./SpaceScanner.types";
/**
 * Space Scanner Modal Props
 */
type SpaceScannerModalProps = {
  onClose: () => void;
};

/**
 * Space Scanner Modal
 *
 * View flow:
 *   0 = Stage 1 (configure scan + optional AI chat)
 *   1 = Stage 2 (review results)
 *   2 = Result (summary after apply)
 */
export const SpaceScannerModal = ({ onClose }: SpaceScannerModalProps) => {
  const [currentView, setCurrentView] = useState<number>(0);
  const [scannerResult, setScannerResult] = useState<ProcessedScanResult[]>([]);
  const [scanConfig, setScanConfig] = useState<ScanConfiguration>({
    allowOverCapacity: false,
    searchSeatedCapacity: false
  });
  const [originalAssignments, setOriginalAssignments] = useState<
    OriginalAssignment[]
  >([]);
  /**
   * Handle on close
   */
  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  /**
   * Handle on result from Stage 1.
   */
  const handleOnResult = useCallback(
    (result: ProcessedScanResult[], config: ScanConfiguration) => {
      setScannerResult(result);
      setScanConfig(config);
      const assignments: OriginalAssignment[] = result.map((r) => ({
        bookingId: r.booking._id,
        previousAreas: r.previousAreas
      }));
      setOriginalAssignments(assignments);
      setCurrentView(1);
    },
    []
  );

  /**
   * Handle on completed
   */
  const handleOnCompleted = useCallback((result: ScanResults) => {
    setScannerResult(Array.isArray(result) ? result : result.results);
    setCurrentView(2);
  }, []);

  /**
   * Handle go back
   */
  const handleGoBack = useCallback(() => {
    if (currentView === 0) {
      onClose();
      return;
    }

    setCurrentView(0);
  }, [currentView, onClose]);

  const heading = `Space Optimiser (${currentView === 0 ? "1" : "2"}/2)`;

  return (
    <DetachedModal
      handleClose={handleOnClose}
      heading={heading}
      width="fit-content"
      // Force full height so flex children (header/content/footer) can scroll
      height={currentView >= 1 ? "calc(100vh - 80px)" : undefined}
    >
      <>
        {currentView === 0 && (
          <SpaceScannerStage1
            onClose={() => {
              onClose();
            }}
            onResult={handleOnResult}
          />
        )}
        {currentView === 1 && (
          <SpaceScannerStage2
            onGoBack={handleGoBack}
            result={scannerResult}
            onCompleted={handleOnCompleted}
            onClose={handleOnClose}
            scanConfig={scanConfig}
            originalAssignments={originalAssignments}
          />
        )}
        {currentView === 2 && (
          <SpaceScannerResult result={scannerResult} onClose={handleOnClose} />
        )}
      </>
    </DetachedModal>
  );
};
